import { ArrowLeft, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import KeyboardDismissView from './KeyboardDismissView';
import { useTheme } from '../../context/ThemeContext';

/**
 * FormPageLayout - Responsive form page layout component
 *
 * On mobile: Full-screen page with header and scrollable content
 * On desktop: Centered card with max-width, can optionally show as page or modal-like
 * In Modern UI: Forms span full width for better use of space
 *
 * @param {string} title - Page/form title
 * @param {React.ReactNode} children - Form content
 * @param {function} onBack - Custom back handler (optional, defaults to navigate(-1))
 * @param {string} backPath - Path to navigate to on back (optional, defaults to previous page)
 * @param {string} maxWidth - Max width class for desktop (default: 'max-w-2xl', ignored in modern UI)
 */
export default function FormPageLayout({
  title,
  children,
  onBack,
  backPath,
  maxWidth = 'max-w-2xl'
}) {
  const navigate = useNavigate();
  const { isModernUI } = useTheme();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  return (
    <>
      {/* Mobile: Full-screen layout */}
      <div className="xl:hidden fixed inset-0 bg-white z-[55] flex flex-col safe-area-top">
        {/* Mobile Header */}
        <div
          className={isModernUI
            ? "flex items-center gap-2 px-3 py-2.5 border-b border-white/10 sticky top-0 z-10 flex-shrink-0"
            : "flex items-center gap-3 px-4 py-3 border-b border-surface-200 bg-white sticky top-0 z-10 flex-shrink-0"
          }
          style={isModernUI ? {
            background: `linear-gradient(135deg, var(--accent-bg), color-mix(in srgb, var(--accent-bg) 85%, black))`,
          } : {}}
        >
          <button
            onClick={handleBack}
            className={isModernUI
              ? "p-1.5 -ml-0.5 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-300 active:scale-90"
              : "p-2 -ml-2 text-surface-600 hover:text-surface-900 rounded-lg transition-colors"
            }
            style={isModernUI ? { color: 'var(--accent-text)' } : {}}
          >
            <ArrowLeft className={isModernUI ? "w-4 h-4" : "w-5 h-5"} />
          </button>
          <h2
            className={isModernUI ? "text-sm font-bold flex-1" : "text-lg font-semibold text-surface-900 flex-1"}
            style={isModernUI ? { color: 'var(--accent-text)' } : {}}
          >
            {title}
          </h2>
        </div>

        {/* Mobile Content - Scrollable */}
        <KeyboardDismissView className={isModernUI ? "flex-1 overflow-y-auto overscroll-contain px-3 py-3 pb-6" : "flex-1 overflow-y-auto overscroll-contain px-4 py-4 pb-8"} style={{ WebkitOverflowScrolling: 'touch', paddingBottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))' }}>
          {children}
        </KeyboardDismissView>
      </div>

      {/* Desktop: Centered card layout (full-width in modern UI) */}
      <div className="hidden xl:block">
        <div className={isModernUI ? 'w-full' : `mx-auto ${maxWidth}`}>
          {/* Desktop Header */}
          <div className={`flex items-center gap-4 mb-6 ${isModernUI ? 'animate-fade-in' : ''}`}>
            <button
              onClick={handleBack}
              className={`p-2 text-surface-500 hover:text-surface-700 rounded-lg transition-colors ${
                isModernUI ? 'hover:bg-surface-100/80 hover:shadow-sm' : 'hover:bg-surface-100'
              }`}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold text-surface-900">{title}</h1>
          </div>

          {/* Desktop Content Card */}
          <div className={`bg-white rounded-2xl border border-surface-200 p-6 ${
            isModernUI ? 'shadow-sm hover:shadow-md transition-shadow duration-300' : ''
          }`}>
            {children}
          </div>
        </div>
      </div>
    </>
  );
}
